/**
 * Custom hook for managing PDF zoom and fit modes
 *
 * Extracted from PDFViewerModal.tsx
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { FitMode } from '../types/pdf-viewer.types';

export interface UsePDFZoomProps {
  containerRef: React.RefObject<HTMLDivElement>;
  pageDimensions: { width: number; height: number } | null;
  isOpen: boolean;
  onWheelZoom?: (deltaY: number) => void;
}

export interface UsePDFZoomReturn {
  scale: number;
  fitMode: FitMode;
  showFitDropdown: boolean;
  fitDropdownPosition: { top: number; right: number } | null;
  fitButtonRef: React.RefObject<HTMLButtonElement>;
  setScale: (scale: number | ((prev: number) => number)) => void;
  setFitMode: (mode: FitMode) => void;
  setShowFitDropdown: (show: boolean) => void;
  setFitDropdownPosition: (pos: { top: number; right: number } | null) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  calculateFitScale: (mode: FitMode) => void;
}

/**
 * Manages PDF zoom and fit mode state and calculations
 */
export function usePDFZoom({
  containerRef,
  pageDimensions,
  isOpen,
  onWheelZoom,
}: UsePDFZoomProps): UsePDFZoomReturn {
  const [scale, setScaleState] = useState<number>(1.2);
  const [fitMode, setFitModeState] = useState<FitMode>('manual');
  const [showFitDropdown, setShowFitDropdown] = useState(false);
  const [fitDropdownPosition, setFitDropdownPosition] = useState<{ top: number; right: number } | null>(null);
  const fitButtonRef = useRef<HTMLButtonElement>(null);

  // Load fit mode from localStorage
  useEffect(() => {
    try {
      const savedFitMode = localStorage.getItem('pdf-viewer-fit-mode');
      if (savedFitMode && ['manual', 'width', 'height', 'page'].includes(savedFitMode)) {
        setFitModeState(savedFitMode as FitMode);
      }
    } catch (error) {
      console.error('Error loading fit mode:', error);
    }
  }, []);

  // Save fit mode to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('pdf-viewer-fit-mode', fitMode);
    } catch (error) {
      console.error('Error saving fit mode:', error);
    }
  }, [fitMode]);

  // Calculate fit scale
  const calculateFitScale = useCallback((mode: FitMode) => {
    if (!containerRef.current || !pageDimensions || mode === 'manual') return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width - 32; // Account for padding
    const containerHeight = containerRect.height - 32;

    const pageWidth = pageDimensions.width;
    const pageHeight = pageDimensions.height;

    let newScale = scale;

    switch (mode) {
      case 'width':
        newScale = containerWidth / pageWidth;
        break;
      case 'height':
        newScale = containerHeight / pageHeight;
        break;
      case 'page':
        newScale = Math.min(containerWidth / pageWidth, containerHeight / pageHeight);
        break;
    }

    // Clamp scale
    newScale = Math.max(0.5, Math.min(2.5, newScale));
    setScaleState(newScale);
  }, [pageDimensions, scale, containerRef]);

  // Handle fit mode changes
  useEffect(() => {
    if (fitMode !== 'manual' && pageDimensions && containerRef.current) {
      calculateFitScale(fitMode);
    }
  }, [fitMode, pageDimensions, calculateFitScale]);

  // Handle window resize for fit modes
  useEffect(() => {
    if (fitMode === 'manual' || !isOpen) return;

    const handleResize = () => {
      if (pageDimensions) {
        calculateFitScale(fitMode);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fitMode, pageDimensions, isOpen, calculateFitScale]);

  // Handle Ctrl+Scroll for zoom
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isOpen) return;

    const handleWheel = (e: WheelEvent) => {
      // Ctrl + Scroll = Zoom (always works)
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();

        if (e.deltaY < 0) {
          // Scroll up = Zoom in
          setScaleState(prev => Math.min(2.5, prev + 0.1));
        } else {
          // Scroll down = Zoom out
          setScaleState(prev => Math.max(0.5, prev - 0.1));
        }
        setFitModeState('manual');
        onWheelZoom?.(e.deltaY);
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [isOpen, containerRef, onWheelZoom]);

  // Close dropdown on scroll/resize
  useEffect(() => {
    if (!showFitDropdown) return;

    const handleClose = () => {
      setShowFitDropdown(false);
      setFitDropdownPosition(null);
    };

    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('resize', handleClose);

    return () => {
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('resize', handleClose);
    };
  }, [showFitDropdown]);

  // Close dropdown when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowFitDropdown(false);
      setFitDropdownPosition(null);
    }
  }, [isOpen]);

  const setScale = useCallback((newScale: number | ((prev: number) => number)) => {
    setScaleState(newScale);
    if (fitMode !== 'manual') {
      setFitModeState('manual');
    }
  }, [fitMode]);

  const setFitMode = useCallback((mode: FitMode) => {
    setFitModeState(mode);
  }, []);

  const zoomIn = useCallback(() => {
    setFitMode('manual');
    setScaleState(prev => Math.min(2.5, prev + 0.2));
  }, []);

  const zoomOut = useCallback(() => {
    setFitMode('manual');
    setScaleState(prev => Math.max(0.5, prev - 0.2));
  }, []);

  return {
    scale,
    fitMode,
    showFitDropdown,
    fitDropdownPosition,
    fitButtonRef,
    setScale,
    setFitMode,
    setShowFitDropdown,
    setFitDropdownPosition,
    zoomIn,
    zoomOut,
    calculateFitScale,
  };
}
